import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bug, X, Image as ImageIcon, Send, Loader2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation, useLanguage } from '../context/LanguageContext';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';
import { toast } from 'sonner';

const MAX_BYTES = 4 * 1024 * 1024;

/**
 * "Segnala bug" button shown in Profilo. Opens a bottom sheet with a
 * textarea + an optional screenshot input. Submits to /api/support/bug.
 */
export default function BugReportButton() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { language } = useLanguage();

  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [screenshot, setScreenshot] = useState(null);   // base64 data URL
  const [screenshotName, setScreenshotName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const close = () => {
    if (submitting) return;
    setOpen(false);
    setText('');
    setScreenshot(null);
    setScreenshotName('');
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(t('support.bugScreenshotTooLarge'));
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setScreenshot(reader.result);
      setScreenshotName(file.name);
    };
    reader.onerror = () => toast.error(t('support.bugScreenshotError'));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      toast.error(t('support.bugTextRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await httpClient.post(`${API_URL}/api/support/bug`, {
        text: trimmed,
        screenshot_base64: screenshot || undefined,
        language,
      });
      toast.success(t('support.bugSent'));
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('support.bugError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full glass-card-sm p-4 flex items-center justify-between"
        data-testid="bug-report-btn"
      >
        <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Bug className={`w-5 h-5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
          <div className="text-left">
            <p>{t('support.reportBug')}</p>
            <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {t('support.reportBugSubtitle')}
            </p>
          </div>
        </span>
      </button>

      {open && createPortal(
        <div className="modal-bottom" onClick={close} data-testid="bug-modal">
          <div
            className="modal-content animate-slide-up"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className={`w-12 h-1 rounded-full mx-auto mb-4 ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />

            <div className="flex items-center justify-between mb-4">
              <h2 className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('support.bugTitle')}
              </h2>
              <button
                onClick={close}
                className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}
              >
                <X className={`w-4 h-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`} />
              </button>
            </div>

            <p className={`text-sm mb-3 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {t('support.bugSubtitle')}
            </p>

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 2000))}
              placeholder={t('support.bugPlaceholder')}
              data-testid="bug-text-input"
              className={`w-full p-3 rounded-xl outline-none mb-3 resize-none ${
                isDark
                  ? 'bg-white/5 text-white placeholder:text-white/30 border border-white/10'
                  : 'bg-gray-100 text-gray-900 placeholder:text-gray-400'
              }`}
              rows={6}
            />

            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleFile}
              className="hidden"
              data-testid="bug-screenshot-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              className={`w-full p-3 rounded-xl flex items-center gap-2 mb-3 ${
                screenshot
                  ? (isDark ? 'bg-emerald-500/10 text-emerald-300' : 'bg-emerald-50 text-emerald-700')
                  : (isDark ? 'bg-white/5 text-white/70' : 'bg-gray-100 text-gray-700')
              }`}
              data-testid="bug-screenshot-pick"
            >
              <ImageIcon className="w-4 h-4" />
              <span className="text-sm flex-1 text-left truncate">
                {screenshot ? screenshotName : t('support.bugScreenshot')}
              </span>
              {screenshot && (
                <span
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setScreenshot(null);
                    setScreenshotName('');
                    if (fileRef.current) fileRef.current.value = '';
                  }}
                  className="text-xs underline opacity-80"
                >
                  {t('common.remove')}
                </span>
              )}
            </button>

            <button
              onClick={submit}
              disabled={submitting || !text.trim()}
              className="w-full py-3 rounded-xl font-semibold bg-[#E8334A] text-white flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="bug-submit"
            >
              {submitting
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <><Send className="w-4 h-4" /> {t('support.bugSend')}</>}
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
